import * as React from 'react';
import {FunctionComponent, useMemo, useState} from 'react';
import {DownloadedIndoorWorkout} from '../../../../Models/DownloadedIndoorWorkout';
import {classNames} from '../../../Support/classNames';
import {calculateSplitTimes, Split, SplitStrategy, Strategy} from '../../../../Business/calculateSplitTimes';
import {Distance} from '../../../../Domain/Distance';
import {formatDuration} from '../../../Support/Formatting/formatDuration';
import {formatAverageSplit} from '../../../Support/Formatting/formatSplit';
import {formatDistance} from '../../../Support/Formatting/formatDistance';
import {Fraction} from '../../../../Domain/Fraction';
import {InfoTooltip} from '../../Utils/InfoTooltip';
import {formatAveragePower} from '../../../Support/Formatting/formatPower';
import {formatAverageStrokeRate} from '../../../Support/Formatting/formatStrokeRate';
import {formatAverageHeartRate} from '../../../Support/Formatting/formatHeartRate';
import {FeatureFlags} from '../../../../FeatureFlags';
import {RangeInput, splitToRangeOverview} from '../../../../Models/RangeInput';
import {formatAveragePowerSplit} from '../../../Support/Formatting/formatPowerSplit';
import {useSettings, PowerMode} from '../../../Settings/settings';
import {formatSplitStrategy} from '../../Utils/FormatSplitStrategy';

interface OwnProps {
    downloadedIndoorWorkout: DownloadedIndoorWorkout;
    highlightedRange?: RangeInput | null;
    onRangeChange?: (range: RangeInput | null) => void;
    className?: string;
}

function hasStrokes(downloadedIndoorWorkout: DownloadedIndoorWorkout) {
    return downloadedIndoorWorkout.workoutIntervals.some((interval) => interval.strokes.length > 0);
}

export const SplitTable: FunctionComponent<OwnProps> = ({
    downloadedIndoorWorkout,
    highlightedRange,
    onRangeChange,
    className,
}) => {
    const [strategy, setStrategy] = useState<SplitStrategy>({
        strategy: Strategy.Distance,
        distance: Distance.fromMeters(500),
    });

    const blocks: Array<{interval: string; splits: Split[]}> = downloadedIndoorWorkout.workoutIntervals.map(
        (workoutInterval, index) => {
            return {
                interval: 'Interval ' + (index + 1),
                splits: calculateSplitTimes(workoutInterval.strokes, strategy),
            };
        },
    );

    const {powerMode} = useSettings();
    const getPowerLabel = () => {
        return powerMode === PowerMode.Split ? 'Ø Split Power' : 'Ø Power';
    };

    if (!hasStrokes(downloadedIndoorWorkout)) {
        return (
            <div className={classNames('card', className)} id="split-times">
                <div className="card-title">
                    <h5>Stroke analyzer</h5>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-12 d-flex">
                            <span className="card-body__empty">No stroke data available</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const hasHeartRate = useMemo(() => {
        return downloadedIndoorWorkout.workoutIntervals.some((interval) =>
            interval.strokes.some((stroke) => stroke.heartRate),
        );
    }, [downloadedIndoorWorkout]);

    return (
        <div className={classNames('card intervaltable', className)} id="split-times">
            <div className="card-title">
                <h5 className="d-flex flex-row justify-content-between">
                    <span>
                        Split times
                        <InfoTooltip
                            className="ms-2"
                            description="Based on interpolated strokes, can differ from interval results"
                        />
                    </span>
                    <div className="dropdown">
                        <button
                            className="btn btn-secondary btn-sm dropdown-toggle"
                            type="button"
                            id="splittablemode"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            {formatSplitStrategy(strategy)}
                        </button>
                        <ul className="dropdown-menu" aria-labelledby="splittablemode">
                            {[100, 200, 500, 1000, 2000]
                                .map((distanceMeters) => Distance.fromMeters(distanceMeters))
                                .map((distance) => {
                                    return (
                                        <li key={'distance-' + distance.meters}>
                                            <button
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setStrategy({
                                                        strategy: Strategy.Distance,
                                                        distance: distance,
                                                    });
                                                }}
                                            >
                                                {formatDistance(distance).formatted} {formatDistance(distance).unit}
                                            </button>
                                        </li>
                                    );
                                })}
                            {[10, 20, 25, 100 / 3, 50]
                                .map((percentage) => Fraction.fromPercentage(percentage))
                                .map((fraction) => {
                                    return (
                                        <li key={'fraction-' + fraction.percentage}>
                                            <button
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setStrategy({
                                                        strategy: Strategy.Fraction,
                                                        fraction: fraction,
                                                    });
                                                }}
                                            >
                                                {fraction.percentage.toFixed(0) + ' %'}
                                            </button>
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                </h5>
            </div>
            <table className="table">
                <thead>
                    <tr>
                        {FeatureFlags.rangeAnalyzer && onRangeChange && (
                            <th className="intervaltable__cell intervaltable__cell--narrow"></th>
                        )}
                        <th />
                        <th className="intervaltable__cell">Duration</th>
                        <th className="intervaltable__cell intervaltable__cell--rightalined">
                            Ø Split <span className="intervaltable__cell-label">(/500m)</span>
                        </th>
                        <th className="intervaltable__cell intervaltable__cell--rightalined">
                            {getPowerLabel()} <span className="intervaltable__cell-label">(W)</span>
                        </th>
                        <th className="intervaltable__cell intervaltable__cell--rightalined">
                            Ø Stroke rate <span className="intervaltable__cell-label">(/min)</span>
                        </th>
                        {hasHeartRate && (
                            <th className="intervaltable__cell intervaltable__cell--rightalined">
                                Ø Heart rate <span className="intervaltable__cell-label">(bpm)</span>
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {blocks.map((block, index) => {
                        let numColumns = 5;
                        if (FeatureFlags.rangeAnalyzer && onRangeChange) {
                            numColumns++;
                        }
                        if (hasHeartRate) {
                            numColumns++;
                        }
                        return (
                            <React.Fragment key={index}>
                                <tr>
                                    <td colSpan={numColumns}>
                                        <span className="badge rounded-pill bg-dark">{block.interval}</span>
                                    </td>
                                </tr>
                                {block.splits.map((split, splitIndex) => {
                                    const start = split.strokes[0].totalTime;
                                    const end = split.strokes[split.strokes.length - 1].totalTime;
                                    const selected =
                                        highlightedRange &&
                                        start.equals(highlightedRange.start) &&
                                        end.equals(highlightedRange.end);
                                    return (
                                        <tr
                                            key={splitIndex}
                                            className={classNames('intervaltable__row', {
                                                'intervaltable__row--highlighted': selected,
                                            })}
                                        >
                                            {FeatureFlags.rangeAnalyzer && onRangeChange ? (
                                                <td className="intervaltable__cell intervaltable__cell--narrow">
                                                    <button
                                                        className={classNames('btn btn-sm', {
                                                            'btn-outline-primary': !selected,
                                                            'btn-primary': selected,
                                                        })}
                                                        onClick={() => {
                                                            if (selected) {
                                                                onRangeChange(null);
                                                                return;
                                                            }
                                                            onRangeChange({
                                                                start: start,
                                                                end: end,
                                                                predetermined: splitToRangeOverview(split),
                                                            });
                                                        }}
                                                    >
                                                        <i className="bi bi-graph-up" />
                                                    </button>
                                                </td>
                                            ) : null}
                                            <td className="intervaltable__cell">
                                                {formatDistance(split.distance).formatted}{' '}
                                                {formatDistance(split.distance).unit}
                                            </td>
                                            <td className="intervaltable__cell">
                                                {formatDuration(split.duration).formatted}
                                            </td>
                                            <td className="intervaltable__cell intervaltable__cell--rightalined">
                                                {formatAverageSplit(split.speed).formatted}
                                            </td>
                                            <td className="intervaltable__cell intervaltable__cell--rightalined">
                                                {powerMode === PowerMode.Split
                                                    ? formatAveragePowerSplit(split.speed).formatted
                                                    : formatAveragePower(split.power).formatted}
                                            </td>
                                            <td className="intervaltable__cell intervaltable__cell--rightalined">
                                                {formatAverageStrokeRate(split.strokeRate).formatted}
                                            </td>
                                            {hasHeartRate && (
                                                <td className="intervaltable__cell intervaltable__cell--rightalined">
                                                    {formatAverageHeartRate(split.heartRate).formatted}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
